import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Reusable Web Speech API dictation hook for plain inputs.
//
// Mirrors the recogniser lifecycle used by the Task comment box: continuous,
// interim results, auto-restart while the user keeps the mic on. The consumer
// receives each *finalised* transcript chunk via `onFinal(rawText)` and is
// responsible for inserting it (e.g. with tokenizeDictation + applyOpsToText).
// ---------------------------------------------------------------------------
export function useDictation(onFinal) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recRef = useRef(null);
  const shouldRef = useRef(false);
  const cbRef = useRef(onFinal);

  useEffect(() => { cbRef.current = onFinal; }, [onFinal]);

  const stop = useCallback(() => {
    shouldRef.current = false;
    setListening(false);
    setInterim('');
    try { recRef.current && recRef.current.stop(); } catch { /* noop */ }
  }, []);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    let rec = recRef.current;
    if (!rec) {
      rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (e) => {
        let finalText = '';
        let interimText = '';
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const seg = e.results[i];
          if (seg.isFinal) finalText += seg[0].transcript;
          else interimText += seg[0].transcript;
        }
        if (finalText && cbRef.current) cbRef.current(finalText);
        setInterim(interimText);
      };
      rec.onerror = (ev) => {
        if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
          shouldRef.current = false;
          setListening(false);
          setInterim('');
        }
      };
      rec.onend = () => {
        setInterim('');
        if (shouldRef.current) { try { rec.start(); } catch { /* noop */ } }
        else setListening(false);
      };
      recRef.current = rec;
    }
    shouldRef.current = true;
    try { rec.start(); setListening(true); } catch { /* already started */ }
  }, []);

  const toggle = useCallback(() => { if (listening) stop(); else start(); }, [listening, start, stop]);

  useEffect(() => () => {
    shouldRef.current = false;
    try { recRef.current && recRef.current.abort(); } catch { /* noop */ }
  }, []);

  return { listening, interim, start, stop, toggle };
}

export default useDictation;
